import { useEffect, useRef } from "react";

/**
 * Desktop scale-to-fit wrapper. On desktop (>=769px), iteratively scales
 * inner content so that the whole view fits without scrolling. Mobile
 * falls back to natural scrolling.
 */
export function ViewWrapper({ children }: { children: React.ReactNode }) {
  const outerRef = useRef<HTMLDivElement | null>(null);
  const innerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let frame1 = 0;
    let frame2 = 0;

    function reset(el: HTMLDivElement) {
      el.style.transform = "";
      el.style.transformOrigin = "";
      el.style.width = "";
    }

    function fit() {
      const outer = outerRef.current;
      const inner = innerRef.current;
      if (!outer || !inner) return;

      const isDesktop = window.matchMedia("(min-width: 769px)").matches;
      if (!isDesktop) {
        reset(inner);
        return;
      }

      const availW = outer.clientWidth;
      const availH = outer.clientHeight;

      let s = 1.0;
      for (let i = 0; i < 6; i++) {
        inner.style.transform = "";
        inner.style.width = `${availW / s}px`;
        // Force reflow so scrollHeight reflects current width
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        inner.scrollHeight;
        const h = inner.scrollHeight;
        const next = Math.max(0.4, Math.min(availH / h, 1.0));
        if (Math.abs(next - s) < 0.0005) {
          s = next;
          break;
        }
        s = next;
      }
      inner.style.transform = `scale(${s})`;
      inner.style.transformOrigin = "top left";
      inner.style.width = `${availW / s}px`;
    }

    function schedule() {
      frame1 = requestAnimationFrame(() => {
        frame2 = requestAnimationFrame(fit);
      });
    }

    schedule();
    window.addEventListener("resize", schedule);
    return () => {
      window.removeEventListener("resize", schedule);
      cancelAnimationFrame(frame1);
      cancelAnimationFrame(frame2);
    };
  }, []);

  return (
    <div
      ref={outerRef}
      style={{
        width: "100%",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <div ref={innerRef} style={{ width: "100%" }}>
        {children}
      </div>
    </div>
  );
}
